import { oauthAuth } from "../standardAuthPatterns";
import {
  defineProduct,
  productVariant,
  type ConnectorOAuthConfig,
} from "../types";
import logo from "./logo.svg";

const publicEnvVars = (
  configuration: Record<string, string>
): Record<string, string> => {
  if (configuration.instanceUrl) {
    return { SALESFORCE_INSTANCE_URL: configuration.instanceUrl };
  }
  // Connectors authorized through credentials.tines.com stored only their My Domain.
  if (configuration.domain) {
    return {
      SALESFORCE_INSTANCE_URL: `https://${configuration.domain}.my.salesforce.com`,
    };
  }
  return {};
};

// Scopes come from the Tines-owned Salesforce app’s configuration, so none are requested.
const salesforceOAuth = (loginHost: string): ConnectorOAuthConfig => ({
  grantType: () => "authorization_code",
  authorizationEndpoint: () => `https://${loginHost}/services/oauth2/authorize`,
  tokenEndpoint: () => `https://${loginHost}/services/oauth2/token`,
  tokenEndpointAuthMethod: () => "client_secret_post",
  clientCredentialsFrom: "managed",
  scopes: () => [],
  authorizationParameters: () => ({ prompt: "consent" }),
  pkce: () => true,
  instanceUrlFromTokenResponse: ({ instance_url }) =>
    salesforceOrigin(instance_url),
});

// Workflows call SALESFORCE_INSTANCE_URL directly, so only an HTTPS origin inside the connector’s URL pattern is kept.
function salesforceOrigin(value: unknown) {
  if (typeof value !== "string" || !URL.canParse(value)) return undefined;
  const url = new URL(value);
  return url.protocol === "https:" && url.hostname.endsWith(".salesforce.com")
    ? url.origin
    : undefined;
}

const KEYWORDS = "sfdc crm sales service cloud soql sobject apex lightning";

export const salesforce = defineProduct({
  name: "Salesforce",
  tagLine: "CRM and business automation",
  logo,
  tags: ["featured", "it", "productivity"],
  variants: [
    productVariant({
      identifier: "salesforce",
      authLabel: "Production",
      keywords: KEYWORDS,
      protocol: "http",
      defaultUrlPattern: () => "https://[^/]*\\.salesforce\\.com/.*",
      auth: oauthAuth,
      steps: [
        {
          title: "Connect your Salesforce account",
          items: [
            {
              kind: "oauth",
            },
          ],
        },
      ],
      oauth: salesforceOAuth("login.salesforce.com"),
      connectionTest: {
        protocol: "http",
        request: () => "https://login.salesforce.com/services/oauth2/userinfo",
      },
      publicEnvVars,
    }),
    productVariant({
      identifier: "salesforceSandbox",
      authLabel: "Sandbox",
      keywords: KEYWORDS,
      protocol: "http",
      defaultUrlPattern: () => "https://[^/]*\\.salesforce\\.com/.*",
      auth: oauthAuth,
      steps: [
        {
          title: "Connect your Salesforce sandbox account",
          items: [
            {
              kind: "oauth",
            },
          ],
        },
      ],
      oauth: salesforceOAuth("test.salesforce.com"),
      connectionTest: {
        protocol: "http",
        request: () => "https://test.salesforce.com/services/oauth2/userinfo",
      },
      publicEnvVars,
    }),
  ],
});
